/**
 * roomSynergies.ts - Room-to-room combination synergy system
 *
 * When specific room types are built together in the same building,
 * synergy bonuses activate. This encourages strategic floor planning.
 *
 * Synergy types:
 * - fear_boost: Increases fear stat
 * - thrill_boost: Increases thrill stat
 * - atmosphere_boost: Increases atmosphere stat
 * - fun_boost: Increases fun stat
 * - scare_multiplier: Global scare multiplier
 * - visitor_bonus: More visitors spawn
 * - sp_bonus: Higher SP earnings
 * - chain_scare: Chain scare bonus increased
 */

import type { RoomTypeId } from './rooms.js';

export type SynergyEffectType =
  | 'fear_boost'
  | 'thrill_boost'
  | 'atmosphere_boost'
  | 'fun_boost'
  | 'scare_multiplier'
  | 'visitor_bonus'
  | 'sp_bonus'
  | 'chain_scare';

export interface SynergyEffect {
  type: SynergyEffectType;
  value: number; // percentage bonus, e.g. 15 = +15%
  nameKey: string; // i18n key for this specific effect's display name
}

export interface RoomSynergyDef {
  id: string;
  nameKey: string;        // i18n key for synergy name
  descKey: string;        // i18n key for synergy description
  requiredRooms: RoomTypeId[];  // ALL these rooms must be present
  minRooms?: number;       // minimum number of requiredRooms present (default: all)
  effects: SynergyEffect[];
  icon: string;           // emoji or icon identifier
}

export const ROOM_SYNERGIES: RoomSynergyDef[] = [
  // ═══ Horror Classic Combos ═══
  {
    id: 'dark_mirror',
    nameKey: 'synergy_dark_mirror',
    descKey: 'synergy_dark_mirror_desc',
    requiredRooms: ['dark_corridor', 'mirror_room'],
    effects: [
      { type: 'atmosphere_boost', value: 15, nameKey: 'syn_fx_dark_mirror_e1' },
      { type: 'fear_boost', value: 10, nameKey: 'syn_fx_dark_mirror_e2' },
    ],
    icon: 'mirror',
  },
  {
    id: 'underground_horror',
    nameKey: 'synergy_underground_horror',
    descKey: 'synergy_underground_horror_desc',
    requiredRooms: ['dark_corridor', 'dungeon', 'coffin_room'],
    minRooms: 2,
    effects: [
      { type: 'fear_boost', value: 20, nameKey: 'syn_fx_underground_horror_e1' },
      { type: 'scare_multiplier', value: 10, nameKey: 'syn_fx_underground_horror_e2' },
    ],
    icon: 'skull',
  },
  {
    id: 'restless_spirits',
    nameKey: 'synergy_restless_spirits',
    descKey: 'synergy_restless_spirits_desc',
    requiredRooms: ['graveyard_room', 'coffin_room', 'morgue'],
    minRooms: 2,
    effects: [
      { type: 'atmosphere_boost', value: 20, nameKey: 'syn_fx_restless_spirits_e1' },
      { type: 'visitor_bonus', value: 10, nameKey: 'syn_fx_restless_spirits_e2' },
    ],
    icon: 'ghost',
  },
  {
    id: 'creepy_collection',
    nameKey: 'synergy_creepy_collection',
    descKey: 'synergy_creepy_collection_desc',
    requiredRooms: ['doll_room', 'mirror_room', 'funhouse'],
    minRooms: 2,
    effects: [
      { type: 'fear_boost', value: 15, nameKey: 'syn_fx_creepy_collection_e1' },
      { type: 'thrill_boost', value: 10, nameKey: 'syn_fx_creepy_collection_e2' },
    ],
    icon: 'doll',
  },
  {
    id: 'mad_scientist',
    nameKey: 'synergy_mad_scientist',
    descKey: 'synergy_mad_scientist_desc',
    requiredRooms: ['lab', 'surgery_room', 'science_lab'],
    minRooms: 2,
    effects: [
      { type: 'thrill_boost', value: 20, nameKey: 'syn_fx_mad_scientist_e1' },
      { type: 'sp_bonus', value: 15, nameKey: 'syn_fx_mad_scientist_e2' },
    ],
    icon: 'potion',
  },
  {
    id: 'hidden_paths',
    nameKey: 'synergy_hidden_paths',
    descKey: 'synergy_hidden_paths_desc',
    requiredRooms: ['secret_passage', 'dark_corridor', 'attic'],
    minRooms: 2,
    effects: [
      { type: 'atmosphere_boost', value: 15, nameKey: 'syn_fx_hidden_paths_e1' },
      { type: 'chain_scare', value: 20, nameKey: 'syn_fx_hidden_paths_e2' },
    ],
    icon: 'door',
  },

  // ═══ Circus Combos ═══
  {
    id: 'circus_nightmare',
    nameKey: 'synergy_circus_nightmare',
    descKey: 'synergy_circus_nightmare_desc',
    requiredRooms: ['funhouse', 'freak_show', 'trapeze_room'],
    minRooms: 2,
    effects: [
      { type: 'fun_boost', value: 20, nameKey: 'syn_fx_circus_nightmare_e1' },
      { type: 'fear_boost', value: 15, nameKey: 'syn_fx_circus_nightmare_e2' },
    ],
    icon: 'circus',
  },

  // ═══ Temple Combos ═══
  {
    id: 'ancient_curse',
    nameKey: 'synergy_ancient_curse',
    descKey: 'synergy_ancient_curse_desc',
    requiredRooms: ['sacrifice_altar', 'curse_chamber', 'mummy_tomb'],
    minRooms: 2,
    effects: [
      { type: 'fear_boost', value: 25, nameKey: 'syn_fx_ancient_curse_e1' },
      { type: 'scare_multiplier', value: 15, nameKey: 'syn_fx_ancient_curse_e2' },
    ],
    icon: 'scroll',
  },
  {
    id: 'hidden_treasures',
    nameKey: 'synergy_hidden_treasures',
    descKey: 'synergy_hidden_treasures_desc',
    requiredRooms: ['treasure_vault', 'underground_river'],
    effects: [
      { type: 'sp_bonus', value: 20, nameKey: 'syn_fx_hidden_treasures_e1' },
      { type: 'visitor_bonus', value: 10, nameKey: 'syn_fx_hidden_treasures_e2' },
    ],
    icon: 'gem',
  },

  // ═══ Pirate Combos ═══
  {
    id: 'ship_of_doom',
    nameKey: 'synergy_ship_of_doom',
    descKey: 'synergy_ship_of_doom_desc',
    requiredRooms: ['captain_cabin', 'cargo_hold', 'davy_jones_locker'],
    minRooms: 2,
    effects: [
      { type: 'atmosphere_boost', value: 15, nameKey: 'syn_fx_ship_of_doom_e1' },
      { type: 'fear_boost', value: 15, nameKey: 'syn_fx_ship_of_doom_e2' },
    ],
    icon: 'anchor',
  },
  {
    id: 'prison horror',
    nameKey: 'synergy_prison_horror',
    descKey: 'synergy_prison_horror_desc',
    requiredRooms: ['brig', 'davy_jones_locker'],
    effects: [
      { type: 'thrill_boost', value: 20, nameKey: 'syn_fx_prison_horror_e1' },
      { type: 'scare_multiplier', value: 10, nameKey: 'syn_fx_prison_horror_e2' },
    ],
    icon: 'chain',
  },

  // ═══ Mine Combos ═══
  {
    id: 'deep_descent',
    nameKey: 'synergy_deep_descent',
    descKey: 'synergy_deep_descent_desc',
    requiredRooms: ['mine_shaft', 'crystal_cave', 'underground_lake'],
    minRooms: 2,
    effects: [
      { type: 'atmosphere_boost', value: 20, nameKey: 'syn_fx_deep_descent_e1' },
      { type: 'visitor_bonus', value: 15, nameKey: 'syn_fx_deep_descent_e2' },
    ],
    icon: 'pickaxe',
  },
  {
    id: 'boom_scare',
    nameKey: 'synergy_boom_scare',
    descKey: 'synergy_boom_scare_desc',
    requiredRooms: ['explosives_storage', 'rail_tunnel'],
    effects: [
      { type: 'thrill_boost', value: 25, nameKey: 'syn_fx_boom_scare_e1' },
      { type: 'chain_scare', value: 15, nameKey: 'syn_fx_boom_scare_e2' },
    ],
    icon: 'explosion',
  },

  // ═══ Castle Combos ═══
  {
    id: 'royal_terror',
    nameKey: 'synergy_royal_terror',
    descKey: 'synergy_royal_terror_desc',
    requiredRooms: ['throne_room', 'tower_chamber', 'wine_cellar'],
    minRooms: 2,
    effects: [
      { type: 'fear_boost', value: 15, nameKey: 'syn_fx_royal_terror_e1' },
      { type: 'sp_bonus', value: 20, nameKey: 'syn_fx_royal_terror_e2' },
    ],
    icon: 'crown',
  },
  {
    id: 'noble_cellar',
    nameKey: 'synergy_noble_cellar',
    descKey: 'synergy_noble_cellar_desc',
    requiredRooms: ['wine_cellar', 'secret_passage'],
    effects: [
      { type: 'atmosphere_boost', value: 15, nameKey: 'syn_fx_noble_cellar_e1' },
      { type: 'sp_bonus', value: 10, nameKey: 'syn_fx_noble_cellar_e2' },
    ],
    icon: 'wine',
  },

  // ═══ Bunker Combos ═══
  {
    id: 'quarantine_protocol',
    nameKey: 'synergy_quarantine_protocol',
    descKey: 'synergy_quarantine_protocol_desc',
    requiredRooms: ['server_room', 'quarantine_zone', 'armory'],
    minRooms: 2,
    effects: [
      { type: 'thrill_boost', value: 20, nameKey: 'syn_fx_quarantine_protocol_e1' },
      { type: 'fear_boost', value: 10, nameKey: 'syn_fx_quarantine_protocol_e2' },
    ],
    icon: 'hazard',
  },

  // ═══ Forest Combos ═══
  {
    id: 'nature_wrath',
    nameKey: 'synergy_nature_wrath',
    descKey: 'synergy_nature_wrath_desc',
    requiredRooms: ['witch_hut', 'swamp_path', 'fairy_ring'],
    minRooms: 2,
    effects: [
      { type: 'atmosphere_boost', value: 20, nameKey: 'syn_fx_nature_wrath_e1' },
      { type: 'scare_multiplier', value: 10, nameKey: 'syn_fx_nature_wrath_e2' },
    ],
    icon: 'leaf',
  },
  {
    id: 'ancient_power',
    nameKey: 'synergy_ancient_power',
    descKey: 'synergy_ancient_power_desc',
    requiredRooms: ['ancient_ruin', 'fairy_ring', 'sacrifice_altar'],
    minRooms: 2,
    effects: [
      { type: 'fear_boost', value: 20, nameKey: 'syn_fx_ancient_power_e1' },
      { type: 'visitor_bonus', value: 15, nameKey: 'syn_fx_ancient_power_e2' },
    ],
    icon: 'ruin',
  },

  // ═══ Cross-theme Combos ═══
  {
    id: 'echo_chamber',
    nameKey: 'synergy_echo_chamber',
    descKey: 'synergy_echo_chamber_desc',
    requiredRooms: ['music_room', 'library', 'dark_corridor'],
    minRooms: 2,
    effects: [
      { type: 'chain_scare', value: 25, nameKey: 'syn_fx_echo_chamber_e1' },
      { type: 'atmosphere_boost', value: 10, nameKey: 'syn_fx_echo_chamber_e2' },
    ],
    icon: 'note',
  },
  {
    id: 'water_horror',
    nameKey: 'synergy_water_horror',
    descKey: 'synergy_water_horror_desc',
    requiredRooms: ['bathroom', 'underground_river', 'underground_lake'],
    minRooms: 2,
    effects: [
      { type: 'fear_boost', value: 15, nameKey: 'syn_fx_water_horror_e1' },
      { type: 'atmosphere_boost', value: 15, nameKey: 'syn_fx_water_horror_e2' },
    ],
    icon: 'water',
  },
  {
    id: 'full_course',
    nameKey: 'synergy_full_course',
    descKey: 'synergy_full_course_desc',
    requiredRooms: ['kitchen', 'bathroom', 'music_room'],
    minRooms: 3,
    effects: [
      { type: 'fun_boost', value: 25, nameKey: 'syn_fx_full_course_e1' },
      { type: 'visitor_bonus', value: 20, nameKey: 'syn_fx_full_course_e2' },
    ],
    icon: 'star',
  },
];

/**
 * Calculate active synergies based on current building rooms
 */
export function calcActiveSynergies(builtRoomTypes: RoomTypeId[]): RoomSynergyDef[] {
  return ROOM_SYNERGIES.filter((synergy) => {
    const minRequired = synergy.minRooms ?? synergy.requiredRooms.length;
    const matchCount = synergy.requiredRooms.filter((rt) => builtRoomTypes.includes(rt)).length;
    return matchCount >= minRequired;
  });
}

/**
 * Aggregate all synergy effects into a single bonus object
 */
export function aggregateSynergyEffects(synergies: RoomSynergyDef[]): Record<SynergyEffectType, number> {
  const result: Record<SynergyEffectType, number> = {
    fear_boost: 0,
    thrill_boost: 0,
    atmosphere_boost: 0,
    fun_boost: 0,
    scare_multiplier: 0,
    visitor_bonus: 0,
    sp_bonus: 0,
    chain_scare: 0,
  };

  for (const synergy of synergies) {
    for (const effect of synergy.effects) {
      result[effect.type] += effect.value;
    }
  }

  return result;
}

/**
 * Get synergy multiplier for a specific stat
 * Returns 1.0 + bonus (e.g. 1.15 for +15%)
 */
export function getSynergyMultiplier(
  effects: Record<SynergyEffectType, number>,
  effectType: SynergyEffectType,
): number {
  return 1 + (effects[effectType] ?? 0) / 100;
}
